import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail, RefreshCw, AlarmClock, CalendarClock, CalendarDays, FileWarning } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader, GlassCard, GradButton, EmptyState, StatCard, fmtMoney } from "@/components/erp";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { Link } from "react-router-dom";
import { NakitUyari } from "@/pages/NakitAkis";
import { Switch } from "@/components/ui/switch";

const TH = "p-3 text-left text-[11px] uppercase tracking-wider text-muted-foreground";
const zaman = (t) => new Date(t).toLocaleString("tr-TR");

const Liste = ({ baslik, satirlar, cek, testid }) => (
  <GlassCard className="overflow-x-auto" data-testid={testid}>
    <div className="px-4 pt-4 pb-2 text-sm font-medium">{baslik} <span className="text-muted-foreground">({satirlar.length})</span></div>
    {satirlar.length === 0 ? <div className="px-4 pb-4 text-xs text-muted-foreground">Kayıt yok.</div> : (
      <table className="w-full text-sm">
        <thead><tr className="border-b border-white/5"><th className={TH}>{cek ? "No" : "Fatura"}</th><th className={TH}>Tedarikçi</th><th className={TH}>Vade</th><th className={`${TH} text-right`}>{cek ? "Tutar" : "Kalan"}</th></tr></thead>
        <tbody>{satirlar.map((s) => (
          <tr key={s.id} className="border-b border-white/5" data-testid={`${testid}-${s.no || s.fatura_no}`}>
            <td className="p-3 font-mono text-xs">{cek ? `${s.no}${s.belge_no ? ` · ${s.belge_no}` : ""}` : s.fatura_no}</td><td className="p-3">{s.tedarikci_unvan}</td>
            <td className="p-3 text-xs">{s.vade_tarihi}<span className="ml-2 text-muted-foreground">{cek ? (s.kalan_gun < 0 ? `${-s.kalan_gun} gün geçti` : s.kalan_gun === 0 ? "bugün" : `${s.kalan_gun} gün kaldı`) : s.gecikme_gun > 0 ? `${s.gecikme_gun} gün gecikti` : ""}</span></td>
            <td className="p-3 text-right font-mono">{fmtMoney(cek ? s.tutar_try : s.kalan_try)}</td>
          </tr>
        ))}</tbody>
      </table>
    )}
  </GlassCard>
);

const AlacakListe = ({ baslik, l, testid }) => (
  <GlassCard className="overflow-x-auto" data-testid={testid}>
    <div className="px-4 pt-4 pb-2 text-sm font-medium">{baslik} <span className="text-muted-foreground">({l.length})</span></div>
    {!l.length ? <div className="px-4 pb-4 text-xs text-muted-foreground">Kayıt yok.</div> : (
      <table className="w-full text-sm"><thead><tr className="border-b border-white/5"><th className={TH}>Cari</th><th className={TH}>Belge</th><th className={TH}>Vade</th><th className={`${TH} text-right`}>Kalan</th></tr></thead>
        <tbody>{l.map((s, j) => <tr key={j} className="border-b border-white/5"><td className="p-3"><Link to={`/cariler/${s.cari_id}`} className="hover:underline">{s.unvan}</Link></td><td className="p-3 font-mono text-xs">{s.belge || s.aciklama}</td>
          <td className="p-3 text-xs whitespace-nowrap">{s.vade.split("-").reverse().join(".")}<span className={`ml-2 ${s.gun < 0 ? "text-red-300" : "text-muted-foreground"}`}>{s.gun < 0 ? `${-s.gun} gün geçti` : s.gun === 0 ? "bugün" : `${s.gun} gün kaldı`}</span></td><td className="p-3 text-right font-mono">{fmtMoney(s.kalan)}</td></tr>)}</tbody></table>)}
  </GlassCard>
);

const Alacaklar = ({ a }) => (
  <div className="mt-8 space-y-3" data-testid="ozet-alacaklar">
    <div className="font-head font-semibold">Tahsil edilecekler (müşteri faturaları) <span className="text-sm text-muted-foreground font-normal">· gecikmiş {fmtMoney(a.toplam.gecikmis)} · bugün {fmtMoney(a.toplam.bugun)} · 7 gün {fmtMoney(a.toplam.hafta)}</span></div>
    <div className="space-y-4"><AlacakListe baslik="Vadesi geçmiş" l={a.gecikmis} testid="ozet-a-gecikmis" /><AlacakListe baslik="Bugün vadeli" l={a.bugun} testid="ozet-a-bugun" /><AlacakListe baslik="7 gün içinde" l={a.hafta} testid="ozet-a-hafta" /></div>
  </div>
);

const CariHatirlatma = () => {
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/vade-hatirlatma/ayar").then((r) => setV(r.data)).catch(() => {}); }, []);
  const degis = async (ac) => { try { await api.put("/vade-hatirlatma/ayar", { vade_hatirlatma_cari: ac }); setV((x) => ({ ...x, vade_hatirlatma_cari: ac })); toast.success(ac ? "Carilere vade hatırlatma e-postası açıldı" : "Carilere e-posta kapatıldı"); } catch (e) { toast.error(errMsg(e)); } };
  if (!v) return null;
  return (
    <GlassCard className="mt-6 p-4 space-y-2" data-testid="ozet-cari-hatirlatma">
      <label className="flex items-center gap-3 text-sm"><Switch data-testid="ozet-cari-hatirlatma-switch" checked={v.vade_hatirlatma_cari} onCheckedChange={degis} />Her sabah 09:00'da vadesi geçen / yaklaşan carilere hatırlatma e-postası gönder</label>
      <p className="text-xs text-muted-foreground">Kapalıyken de bayiler vade uyarılarını ve ekstrelerini bayi portalında görür. Her cariye günde en fazla bir e-posta gider.</p>
      {v.son_gonderimler.length > 0 && <div className="text-xs text-muted-foreground">Son gönderimler: {v.son_gonderimler.slice(0, 5).map((s) => `${s.adres} (${s.tarih}, ${s.durum === "gonderildi" ? "gönderildi" : "başarısız"})`).join(" · ")}</div>}
    </GlassCard>
  );
};

const Gecmis = ({ list }) => (
  <div className="space-y-3" data-testid="ozet-gecmis">{list.map((o) => (
    <GlassCard key={o.id} className="p-4 text-xs" data-testid={`ozet-gecmis-${o.tarih}`}>
      <div className="flex flex-wrap justify-between gap-2 text-sm"><span className="font-mono">{o.tarih}</span>
        <span className="text-muted-foreground">gecikmiş {fmtMoney(o.icerik?.toplam?.gecikmis)} · bugün {fmtMoney(o.icerik?.toplam?.bugun)} · çek {fmtMoney(o.icerik?.toplam?.cekler)}</span></div>
      <div className="mt-2 text-muted-foreground">Görenler: {(o.okuyanlar || []).map((x) => `${x.ad} (${zaman(x.tarih)})`).join(", ") || "henüz kimse"}</div>
      <div className="mt-1 text-muted-foreground">E-posta: {(o.eposta_gonderimleri || []).map((g) => `${g.kullanici} · ${zaman(g.tarih)} · ${g.sonuclar.filter((s) => s.durum === "gonderildi").length}/${g.sonuclar.length} gönderildi`).join(" | ") || "gönderilmedi"}</div>
      <div className="mt-1 text-muted-foreground">İşlemler: {(o.islemler || []).map((x) => `${x.olay} · ${x.kaynak} · ${zaman(x.tarih)}`).join(" | ")}</div>
    </GlassCard>
  ))}</div>
);

const EpostaOnay = ({ ozet, onClose, onDone }) => {
  const [alicilar, setAlicilar] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/bildirim/eposta-alicilar").then((r) => setAlicilar(r.data)); }, []);
  const gonder = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/bildirim/gunluk-ozet/${ozet.id}/eposta`);
      const ok = data.sonuclar.filter((s) => s.durum === "gonderildi").length;
      toast[ok === data.sonuclar.length ? "success" : "error"](`${ok}/${data.sonuclar.length} alıcıya gönderildi`);
      onDone();
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="glass" data-testid="ozet-eposta-onay">
        <AlertDialogHeader>
          <AlertDialogTitle>Özet e-postayla gönderilsin mi?</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="text-sm">
            {alicilar === null ? "Alıcılar yükleniyor…" : alicilar.length === 0 ? "Alıcı yok. Ayarlar > Bildirimler'den adres ekleyin veya muhasebe kullanıcısı tanımlayın."
              : <>Şu adreslere gönderilecek:<ul className="mt-2 space-y-1 font-mono text-xs" data-testid="ozet-alicilar">{alicilar.map((a) => <li key={a}>{a}</li>)}</ul></>}
          </div></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ozet-eposta-vazgec">Vazgeç</AlertDialogCancel>
          <AlertDialogAction data-testid="ozet-eposta-onayla" disabled={busy || !alicilar?.length} onClick={gonder}>Gönder</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function GunlukOzet() {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [o, setO] = useState(undefined);
  const [gecmis, setGecmis] = useState([]);
  const [onay, setOnay] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/bildirim/gunluk-ozet");
    setO(data);
    if (data && !data.okundu) api.post(`/bildirim/gunluk-ozet/${data.id}/oku`);
    if (admin) setGecmis((await api.get("/bildirim/gunluk-ozetler")).data);
  }, [admin]);
  useEffect(() => { load(); }, [load]);
  const yenile = async () => { try { await api.post("/bildirim/gunluk-ozet/yenile"); toast.success("Özet güncellendi"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (o === undefined) return null;
  const i = o?.icerik;
  return (
    <div data-testid="gunluk-ozet-page">
      <PageHeader eyebrow="Finans" title="Günlük Özet" desc="Her sabah 09:00'da hazırlanır; yalnızca muhasebe, yönetici ve admin görür. E-posta yalnızca onayınızla gönderilir.">
        <div className="flex gap-2">
          {admin && <button data-testid="ozet-yenile" onClick={yenile} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border border-white/10 hover:bg-white/10"><RefreshCw className="w-4 h-4" />Şimdi yenile</button>}
          {o && <GradButton data-testid="ozet-eposta" onClick={() => setOnay(true)}><Mail className="w-4 h-4" />E-postayla gönder</GradButton>}
        </div>
      </PageHeader>
      {!o ? <GlassCard><EmptyState text="Henüz özet hazırlanmadı. İlk özet yarın sabah 09:00'da oluşur." testid="ozet-bos" /></GlassCard> : (
        <Tabs defaultValue="ozet">
          <TabsList className="bg-white/5 mb-5"><TabsTrigger value="ozet" data-testid="tab-ozet">{o.tarih} özeti</TabsTrigger>{admin && <TabsTrigger value="gecmis" data-testid="tab-ozet-gecmis">Geçmiş & takip</TabsTrigger>}</TabsList>
          <TabsContent value="ozet">
            <div className="text-xs text-muted-foreground mb-4" data-testid="ozet-zaman">Hazırlandı: {zaman(o.guncelleme || o.olusturma)}{o.eposta_gonderimleri?.length ? ` · son e-posta ${zaman(o.eposta_gonderimleri.at(-1).tarih)}` : ""}</div>
            <NakitUyari u={i.nakit} testid="ozet-nakit-uyari" />
            {(i.nakit?.acik || i.nakit?.min_alti) && <Link to="/nakit-akis" className="text-xs text-[var(--g1)] -mt-4 mb-5 inline-block" data-testid="ozet-nakit-link">Nakit akışı grafiğini aç →</Link>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard testid="ozet-gecikmis" label="Vadesi geçmiş" value={fmtMoney(i.toplam.gecikmis)} hint={`${i.gecikmis.length} fatura`} icon={AlarmClock} />
              <StatCard testid="ozet-bugun" label="Bugün vadeli" value={fmtMoney(i.toplam.bugun)} hint={`${i.bugun.length} fatura`} icon={CalendarClock} delay={60} />
              <StatCard testid="ozet-hafta" label="7 gün içinde" value={fmtMoney(i.toplam.hafta)} hint={`${i.hafta.length} fatura`} icon={CalendarDays} delay={120} />
              <StatCard testid="ozet-cekler" label={`Verilen çek (${i.cek_uyari_gun} gün)`} value={fmtMoney(i.toplam.cekler)} hint={`${i.cekler.length} çek / senet`} icon={FileWarning} delay={180} />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <Liste baslik="Vadesi geçmiş faturalar" satirlar={i.gecikmis} testid="ozet-l-gecikmis" />
              <Liste baslik="Bugün vadesi gelen" satirlar={i.bugun} testid="ozet-l-bugun" />
              <Liste baslik="7 gün içinde" satirlar={i.hafta} testid="ozet-l-hafta" />
              <Liste baslik="Vadesi yaklaşan verilen çek / senetler" satirlar={i.cekler} cek testid="ozet-l-cekler" />
            </div>
            {i.alacaklar && <Alacaklar a={i.alacaklar} />}
            {admin && <CariHatirlatma />}
          </TabsContent>
          {admin && <TabsContent value="gecmis"><Gecmis list={gecmis} /></TabsContent>}
        </Tabs>
      )}
      {onay && <EpostaOnay ozet={o} onClose={() => setOnay(false)} onDone={() => { setOnay(false); load(); }} />}
    </div>
  );
}
